import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { STORAGE_UNITS } from '../specimens/StoragePanel';
import { Field, fieldProps } from './IntakeFields';
import { BANK_STOCK_METHODS, MAX_TUBES, ORGANISM_MAX, nextTubeCode, nextWells, wellAfter } from './intakeSource';
import { fullRow, gridStyle, hintStyle, inputStyle, labelStyle, sectionHeading, smallBtn } from './styles';
import MediumOptions from '../culture/MediumOptions';

/*
 * Bank stock: tubes that arrive already preserved -- cryovials, glycerol
 * stocks, a repository's tube -- registered and banked in one request
 * (POST /slants/external-intake with a place). In the order a person reads
 * them off the box:
 *
 *   the tubes      a code each (the intake's code, S and a number, editable),
 *                  a role and the tube's own well
 *   preservation   how they are kept, and the medium they grew on (optional)
 *   the place      the unit, its Freezer ID, rack and box; a first well fills
 *                  every tube's well in order (A1, A2, A3 ...)
 *   the record     when they were preserved, by whom and as what organism --
 *                  what every banked tube must say
 *
 * `value` is intakeSource.defaultBankStock's shape; the rules are
 * intakeSource.bankStockErrors's, shown here beside their fields.
 */
export default function BankStockFields({ value, onChange, protocols, currentUser, qrUid, today, t, errors = {},
  idPrefix = 'intake' }) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    let live = true;
    api.get('/users/names')
      .then((res) => { if (live) setUsers(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const update = (patch) => onChange({ ...value, ...patch });
  const set = (key) => (e) => update({ [key]: e.target.value });
  const setEntry = (i, patch) => update({ entries: value.entries.map((en, idx) => (idx === i ? { ...en, ...patch } : en)) });
  // A new tube takes the lowest number no tube has (S2 again after S2 was
  // removed), and the well after the last tube's.
  const addEntry = () => {
    const last = value.entries[value.entries.length - 1];
    update({ entries: [...value.entries, { code: nextTubeCode(qrUid, value.entries), role: null,
      well: wellAfter(last?.well) }] });
  };
  const removeEntry = (i) => update({ entries: value.entries.filter((_, idx) => idx !== i) });
  // The first well fills every tube's, in order; a tube's own well can still
  // be changed after.
  const setFirstWell = (e) => {
    const wells = nextWells(e.target.value, value.entries.length);
    update({ firstWell: e.target.value, entries: value.entries.map((en, i) => ({ ...en, well: wells[i] })) });
  };

  // Whoever is chosen stays in the list while the names load, or if they are
  // not among the active accounts the server lists.
  const people = [...users];
  if (value.preservedBy && !people.some((u) => u.user_id === value.preservedBy)) {
    people.unshift({ user_id: value.preservedBy,
      username: value.preservedBy === currentUser?.user_id ? (currentUser?.username || value.preservedBy) : value.preservedBy });
  }
  const id = (name) => `${idPrefix}-${name}`;

  return (
    <div data-testid="intake-bank-stock">
      <p style={sectionHeading}>{t('source.bank.tubesHeading')}</p>
      {value.entries.map((entry, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
          {/* "At least one code" and "no code twice" are said on the first. */}
          <Field id={id(`slant-code-${i}`)} field={i === 0 ? 'slantCode' : undefined}
            label={t('source.bank.tubeCode', { n: i + 1 })} error={i === 0 ? errors.slantCode : null} t={t}
            style={{ flex: '2 1 150px' }}>
            <input value={entry.code} onChange={(e) => setEntry(i, { code: e.target.value })}
              {...fieldProps(id(`slant-code-${i}`), i === 0 ? errors.slantCode : null)} dir="ltr"
              title={t('source.tip.tubeCode')} />
          </Field>
          <label style={{ ...labelStyle, flex: '1 1 110px' }}>
            {t('intake.slantRoleLabel')}
            <select value={entry.role || ''} onChange={(e) => setEntry(i, { role: e.target.value || null })}
              style={inputStyle} title={t('tip.slantRole')}>
              <option value="">{t('intake.slantRoleNone')}</option>
              <option value="active">{t('slantRole.active', { ns: 'slants' })}</option>
              <option value="refresh">{t('slantRole.refresh', { ns: 'slants' })}</option>
              <option value="backup">{t('slantRole.backup', { ns: 'slants' })}</option>
            </select>
          </label>
          <Field id={id(`tube-well-${i}`)} field={`tubeWell${i}`} label={t('slants:storage.well')}
            error={errors[`tubeWell${i}`]} t={t} style={{ flex: '1 1 80px' }}>
            <input value={entry.well} onChange={(e) => setEntry(i, { well: e.target.value })} maxLength={5} dir="ltr"
              {...fieldProps(id(`tube-well-${i}`), errors[`tubeWell${i}`], { textTransform: 'uppercase' })}
              title={t('source.tip.tubeWell')} data-testid={`intake-tube-well-${i}`} />
          </Field>
          {value.entries.length > 1 && (
            <button type="button" onClick={() => removeEntry(i)} title={t('source.tip.removeTube')}
              aria-label={t('source.tip.removeTube')} className="touch-target"
              style={{ alignSelf: 'flex-end', background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--c-text-muted-fg)', fontSize: 18, padding: '4px 8px' }}>×</button>
          )}
        </div>
      ))}
      {value.entries.length < MAX_TUBES && (
        <button type="button" onClick={addEntry} style={{ ...smallBtn('var(--c-text-muted)'), marginBottom: 14 }}
          title={t('source.tip.addTube')} data-testid="intake-add-tube">{t('source.bank.addTube')}</button>
      )}

      <div style={gridStyle}>
        <label style={labelStyle}>
          {t('specimens:storage.method')}
          <select value={value.method} onChange={set('method')} style={inputStyle} title={t('source.tip.method')}
            data-testid="intake-bank-method">
            {BANK_STOCK_METHODS.map((m) => <option key={m} value={m}>{t(`specimens:storage.methods.${m}`)}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          {t('source.bank.medium')}
          <select value={value.protocol} onChange={set('protocol')} style={inputStyle} title={t('tip.slantProtocol')}>
            <option value="">{t('selectProtocol', { ns: 'plates' })}</option>
            <MediumOptions protocols={protocols} />
            <option value="other">{t('other', { ns: 'plates' })}</option>
          </select>
        </label>
        {value.protocol === 'other' && (
          <label style={labelStyle}>
            {t('intake.slantProtocolOtherLabel')}
            <input value={value.protocolOther} onChange={set('protocolOther')} style={inputStyle} title={t('tip.protocolOther')} />
          </label>
        )}
      </div>

      <p style={sectionHeading}>{t('source.bank.placeHeading')}</p>
      <div style={gridStyle}>
        <Field id={id('bank-unit')} field="unit" label={t('slants:storage.unit')} error={errors.unit} t={t}>
          <select value={value.unit} onChange={set('unit')} aria-required="true"
            {...fieldProps(id('bank-unit'), errors.unit)} title={t('source.tip.unit')}>
            <option value="">—</option>
            {STORAGE_UNITS.map((u) => <option key={u} value={u}>{t(`specimens:storage.units.${u}`)}</option>)}
          </select>
        </Field>
        <label style={labelStyle}>
          {t('slants:storage.freezerId')}
          <input value={value.unitLabel} onChange={set('unitLabel')} maxLength={60} style={inputStyle}
            placeholder={t('slants:storage.freezerIdPlaceholder')} title={t('source.tip.freezerId')}
            data-testid="intake-bank-unit-label" />
        </label>
        {/* Rack, box and first well are short: three to a row, even on a phone. */}
        <div style={{ ...fullRow, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
          <label style={labelStyle}>
            {t('slants:storage.rack')}
            <input value={value.rack} onChange={set('rack')} maxLength={60} dir="ltr" style={inputStyle}
              title={t('source.tip.rack')} data-testid="intake-bank-rack" />
          </label>
          <label style={labelStyle}>
            {t('slants:storage.box')}
            <input value={value.box} onChange={set('box')} maxLength={60} dir="ltr" style={inputStyle}
              title={t('source.tip.box')} data-testid="intake-bank-box" />
          </label>
          <label style={labelStyle}>
            {t('source.bank.firstWell')}
            <input value={value.firstWell} onChange={setFirstWell} maxLength={5} dir="ltr"
              style={{ ...inputStyle, textTransform: 'uppercase' }} placeholder="A1" aria-describedby="intake-first-well-hint"
              title={t('source.tip.firstWell')} data-testid="intake-bank-first-well" />
          </label>
          <span id="intake-first-well-hint" style={{ ...hintStyle, ...fullRow, margin: 0 }}>{t('source.bank.firstWellHint')}</span>
        </div>
      </div>

      <p style={sectionHeading}>{t('source.bank.recordHeading')}</p>
      <p style={{ ...hintStyle, marginTop: -4 }}>{t('source.bank.recordHint')}</p>
      <div style={{ ...gridStyle, marginBottom: 0 }}>
        <Field id={id('bank-preserved-at')} field="preservedAt" label={t('slants:storage.preservedAt')}
          error={errors.preservedAt} t={t}>
          <input type="date" value={value.preservedAt} onChange={set('preservedAt')} max={today || undefined}
            aria-required="true" {...fieldProps(id('bank-preserved-at'), errors.preservedAt)}
            title={t('source.tip.preservedAt')} />
        </Field>
        <Field id={id('bank-preserved-by')} field="preservedBy" label={t('slants:storage.preservedBy')}
          error={errors.preservedBy} t={t}>
          <select value={value.preservedBy} onChange={set('preservedBy')} aria-required="true"
            {...fieldProps(id('bank-preserved-by'), errors.preservedBy)} title={t('source.tip.preservedBy')}>
            <option value="">—</option>
            {people.map((u) => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
          </select>
        </Field>
        <Field id={id('bank-organism')} field="organism" label={t('slants:storage.organism')}
          error={errors.organism} t={t}>
          <input value={value.organism} onChange={set('organism')} maxLength={ORGANISM_MAX} dir="ltr" aria-required="true"
            {...fieldProps(id('bank-organism'), errors.organism)} title={t('source.tip.organism')} />
        </Field>
      </div>
    </div>
  );
}
